/**
 * useManifold — load + initialise the manifold-3d WASM module exactly once
 * (FXC-9009). The initialised `{ Manifold, Mesh }` object is the `ManifoldModule`
 * that the client-side wing-shell builder (`geometry/wingShell.ts`) expects, so
 * `WingViewer3D` can carve the real 3-D flapped wing entirely in the browser with
 * no backend round-trip.
 *
 * The WASM is loaded through a module-level singleton promise so re-mounting the
 * viewer (e.g. toggling the 2D↔3D switch) reuses the already-initialised module
 * rather than re-downloading / re-`setup()`-ing it.
 *
 * Loading follows manifold's documented bundler recipe: the default export is an
 * emscripten factory that fetches `manifold.wasm` via `locateFile`; we hand it the
 * hashed asset URL Vite emits for `manifold-3d/manifold.wasm?url`.
 */
import { useEffect, useState } from "react";
import Module from "manifold-3d";
import wasmUrl from "manifold-3d/manifold.wasm?url";

import type { ManifoldModule } from "../highlift/geometry/wingShell";

let modulePromise: Promise<ManifoldModule> | null = null;

/** Load + init the manifold-3d WASM once (module-level singleton). */
export function loadManifold(): Promise<ManifoldModule> {
  if (!modulePromise) {
    modulePromise = Module({ locateFile: () => wasmUrl }).then((wasm) => {
      wasm.setup();
      return { Manifold: wasm.Manifold, Mesh: wasm.Mesh };
    });
  }
  return modulePromise;
}

export interface UseManifoldState {
  /** The initialised module, or null while the WASM is still loading. */
  manifold: ManifoldModule | null;
  /** A load/init error, if the WASM failed to come up. */
  error: Error | null;
}

/** React hook: resolves to the initialised manifold-3d module once ready. */
export function useManifold(): UseManifoldState {
  const [state, setState] = useState<UseManifoldState>({ manifold: null, error: null });

  useEffect(() => {
    let alive = true;
    loadManifold().then(
      (m) => {
        if (alive) setState({ manifold: m, error: null });
      },
      (e) => {
        if (alive) setState({ manifold: null, error: e instanceof Error ? e : new Error(String(e)) });
      },
    );
    return () => {
      alive = false;
    };
  }, []);

  return state;
}
